import { ServiceConfig } from '../services/config'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  APIError,
  APIReject,
  APIResponse,
  APIValidationError,
  get,
  post,
  put,
  remove,
} from '../services/crud'
import {
  IOpportunity,
  IOpportunityStatusUpdate,
  ICancelOpportunityServicesInput,
  IOpportunityNextActionDateUpdate,
  IOpportunityPostModel,
  IOpportunityDetail,
  IOpportunityExpectedClosingDateUpdate,
  OpportunityInfo,
  OpportunityName,
  IOpportunityDTO,
  IOpportunityIsCloseableResponse,
  IOpportunityCloseableUpdateModel,
  IOpportunityReOpenModel,
  IOpportunityContactUpdate,
  IOpportunityContactUpdateResponse,
  IOpportunityContactDeleteModel,
  IPrimaryContactModel,
  IOpportunityContactAttach,
  ICloseRequirements,
  CloseWinOpportunityInput,
  IExternalEntityServicesModel,
  IGetCompletionDetailsResponseModel,
  IActiveServiceDetail,
  IRejectedNoteDTO,
  BranchInfoDTO,
  SplitOpportunityInput,
} from './typings'
import { StoreData } from '../reducers/typings'
import buildQuery from 'odata-query'
import { ODataResponse } from '@sales-center/common'
import {
  ISalesTeamReassignDTO,
  ISalesTeamReassignUpdate,
} from '@sales-center/common'
import { IContactInput, IPushContactInput } from '../contacts/typings'
import { checkNotSelectedCampaignIdAndSourceId } from '@user/utils/CheckSourceAndCampaign'
import {
  BulkMode,
  BulkUpdateStatusInput,
  BulkUpdateStatusDTO,
} from '@user/components/xGrid'
import {
  BulkReopenDTO,
  BulkReopenInput,
} from '@user/components/xGrid/atoms/BulkActions/typings/BulkReopen'
import {
  BulkReassignDTO,
  BulkReassignInput,
} from '@user/components/xGrid/atoms/BulkActions/typings/BulkReassign'
import {
  IServiceLocationDetails,
  ILocationDetailParams,
} from '../locations/typings'
import {
  IReferralUpdate,
  IReferralUpdatePostModel,
  IServiceTax,
  IServiceTaxUpdate,
} from '../serviceTax/typings'
import { IBulkNextActionDateInput } from '@user/components/xGrid/atoms/BulkActions/typings/BulkUpdateNextActionDate/BulkNextActionDateInput'
import { BulkUpdateExpectedClosingDateDTO } from '@user/components/xGrid/atoms/BulkActions/typings/BulkUpdateExpectedClosingDate/BulkUpdateExpectedClosingDateDTO'
import { BulkUpdateExpectedClosingDateInput } from '@user/components/xGrid/atoms/BulkActions/typings/BulkUpdateExpectedClosingDate/BulkUpdateExpectedClosingDateInput'
import { setEntityLocations } from '../entityLocations'
import { IExportInput } from '../lead/typings'
import { BulkCloseWinOpportunityInput } from '@user/components/xGrid/atoms/BulkActions/typings/BulkCloseWin/BulkCloseWinOpportunityInput'
import { ENTITY_TYPES } from '@sales-center/common'
import { applyClosedWonFilter } from './helpers'

const baseUrl = `${ServiceConfig.baseAPIUrl}/Opportunity`
const odataUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.opportunity.odata}`
const odataExportUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.opportunity.odataExport}`
const contactBaseUrl = `${ServiceConfig.baseAPIUrl}/Contact`
const secondaryContactBaseUrl = `${ServiceConfig.baseAPIUrl}/OpportunitySecondaryContact`

const baseExpand = ['assignee', 'billToContact', 'serviceContact', 'services']

//========== C R E A T E   O P P O R T U N I T Y ==========
export const createOpportunity = createAsyncThunk<
  IOpportunity,
  IOpportunityPostModel,
  { rejectValue: APIReject }
>('opportunity/add', async (opportunity, thunkApi) => {
  if (checkNotSelectedCampaignIdAndSourceId(opportunity.campaignId)) {
    opportunity.campaignId = null
    opportunity.campaign = null
  }

  if (checkNotSelectedCampaignIdAndSourceId(opportunity.leadSourceId)) {
    opportunity.leadSourceId = null
    opportunity.leadSource = null
  }

  const response = await post(`${baseUrl}`, opportunity)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getOpportunityExport = createAsyncThunk<
  string,
  IExportInput,
  { rejectValue: APIError }
>('opportunity/cards/export', async (params, thunkApi) => {
  const {
    opportunitiesState: {
      filterSet: { normalizedFilters, orderBy },
      mode,
    },
  } = thunkApi.getState() as StoreData

  const expand =
    mode === 'list' ? ['assignee', 'createdBy', 'services'] : ['assignee']

  const modifiedFilters = applyClosedWonFilter(normalizedFilters)

  const qstring = buildQuery<IOpportunity>({
    skip: 0,
    top: 0,
    orderBy,
    expand,
    filter: modifiedFilters,
  })

  const response = await get(
    `${odataExportUrl}${qstring}&selectedColumns=${params.columns}&showMultipleLocations=${params.multiServiceLocations}`,
  )

  if (response.ok) {
    return await (response as Response).text()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const hasMultiServiceLocations = createAsyncThunk<
  ODataResponse<IOpportunity>,
  void,
  { rejectValue: APIError }
>('opp/cards/multiServiceLocations', async (columns, thunkApi) => {
  const expand = ['assignee', 'createdBy', 'services']

  const {
    opportunitiesState: {
      filterSet: { normalizedFilters },
    },
  } = thunkApi.getState() as StoreData

  const qstring = buildQuery<IOpportunity>({
    skip: 0,
    top: 1,
    expand,
    filter: [...normalizedFilters, '(ServiceLocationsCount gt 1)'],
  })

  const response = await get(`${odataUrl}${qstring}&selectedColumns=${columns}`)

  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getOpportunities = createAsyncThunk<
  ODataResponse<IOpportunity>,
  boolean | undefined,
  { rejectValue: APIError }
>('opportunity/paged', async (cardCaptureRefresh = true, thunkApi) => {
  const {
    opportunitiesState: {
      page,
      perPage,
      filterSet: { normalizedFilters, orderBy, search },
      mode,
    },
  } = thunkApi.getState() as StoreData
  const expand =
    mode === 'card'
      ? [...baseExpand]
      : [
          ...baseExpand,
          'createdBy',
          'services',
          'odataCustomFieldValues',
          'leadCreatedBy',
        ]

  const modifiedFilters = applyClosedWonFilter(normalizedFilters)

  const qstring = buildQuery<IOpportunity>({
    skip: (page - 1) * perPage,
    top: perPage,
    count: true,
    expand,
    orderBy,
    filter: modifiedFilters,
  })

  const response = await get(
    `${odataUrl}${qstring}&cardCaptureRefresh=${cardCaptureRefresh}&contactSearch=${!!search?.value}`,
  )

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getOpportunity = createAsyncThunk<IOpportunity, number>(
  'getOpportunity',
  async (id, thunkApi) => {
    const response = await get(`${baseUrl}/${id}`)
    if (response.ok) {
      const payload: IOpportunity = await response.json()
      const primaryContact = payload.primaryContact
      if (payload.nextActionDate !== null)
        payload.nextActionDate = new Date(payload.nextActionDate)
      if (payload.closedDate !== null)
        payload.closedDate = new Date(payload.closedDate)
      if (payload.createdDate !== null)
        payload.createdDate = new Date(payload.createdDate)
      if (payload.updatedDate !== null)
        payload.updatedDate = new Date(payload.updatedDate)
      if (payload.expectedClosingDate !== null)
        payload.expectedClosingDate = new Date(payload.expectedClosingDate)
      return {
        ...payload,
        primaryContact: {
          ...primaryContact,
          firstName: primaryContact.firstName ?? '',
          lastName: primaryContact.lastName ?? '',
        },
      }
    }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getOpportunityValue = createAsyncThunk<
  ODataResponse<IOpportunity>,
  number,
  { rejectValue: APIError }
>('opportunity/values', async (id, thunkApi) => {
  const qstring = buildQuery<IOpportunity>({
    filter: { id },
  })
  const response = await get(`${odataUrl}${qstring}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getBranchesForOpportunities = createAsyncThunk<
  BranchInfoDTO[],
  number[]
>('Opportunity/BranchInfo', async (model, thunkApi) => {
  const response = await post(`${baseUrl}/GetBranchesForOpportunities`, model)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

// ========== R E - O P E N   O P P O R T U N I T Y ==========
export const reOpenOpportunity = createAsyncThunk<
  void,
  IOpportunityReOpenModel,
  { rejectValue: APIError }
>('reOpenOpportunity', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/reopen`, data)
  if (!response.ok) {
    return thunkApi.rejectWithValue((await response.json()) as APIError)
  }
})

// ========== U P D A T E   O P P O R T U N I T Y   E X P E C T E D   C L O S I N G   D A T E ==========
export const updateOpportunityExpectedClosingDate = createAsyncThunk<
  IOpportunityExpectedClosingDateUpdate,
  IOpportunityExpectedClosingDateUpdate,
  { rejectValue: APIError }
>('updateOpportunityExpectedClosingDate', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/expectedClosingDate`, data)
  if (response.ok) {
    return data
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== U P D A T E   O P P O R T U N I T Y   N E X T   A C T I O N   D A T E ==========
export const updateOpportunityNextActionDate = createAsyncThunk<
  IOpportunityNextActionDateUpdate,
  IOpportunityNextActionDateUpdate,
  { rejectValue: APIError }
>('updateOpportunityNextActionDate', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/nextActionDate`, data)
  if (response.ok) {
    return data
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== U P D A T E   O P P O R T U N I T Y   S T A T U S ==========
export const updateOpportunityStatus = createAsyncThunk<
  IOpportunityStatusUpdate,
  IOpportunityStatusUpdate,
  { rejectValue: APIError }
>('updateOpportunityStatus', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/updateStatus`, data)
  if (response.ok) {
    const payload: IOpportunityStatusUpdate = await response.json()

    payload.closedDate = payload.closedDate
      ? new Date(payload.closedDate)
      : null

    payload.opportunityId = data.opportunityId
    payload.customReason = payload.customReason || data.customReason
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const cancelOpportunityServices = createAsyncThunk<
  ICancelOpportunityServicesInput,
  ICancelOpportunityServicesInput,
  { rejectValue: APIError }
>('opportunity/cancelServices', async (data, thunkApi) => {
  const response = await post(
    `${baseUrl}/${data.opportunityId}${ServiceConfig.opportunity.cancelActiveServices}`,
    {
      opportunityId: data.opportunityId,
      serviceIds: data.serviceIds,
      notServicedReason: data.notServicedReason,
    },
  )
  if (response.ok) {
    return data
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const closeWinOpportunity = createAsyncThunk<
  void,
  CloseWinOpportunityInput,
  { rejectValue: APIError | ICloseRequirements[] }
>('opp/closeWin', async (input, thunkApi) => {
  const response = await put(`${baseUrl}/closeWin`, input)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})

export const deleteOpportunity = createAsyncThunk<
  number,
  number,
  { rejectValue: APIError }
>('opportunity/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)
  if (response.ok) return id
  return thunkApi.rejectWithValue(await response.json())
})

export const getOpportunityDetail = createAsyncThunk<
  IOpportunityDetail,
  number,
  { rejectValue: APIError }
>('opportunity/getOpportunityDetail', async (opportunityId, thunkApi) => {
  const response = await get(`${baseUrl}/${opportunityId}`)

  if (response.ok) {
    const payload: IOpportunityDetail = await response.json()

    thunkApi.dispatch(setEntityLocations(payload.locations))

    return {
      ...payload,
      entityType: ENTITY_TYPES.Opportunity,
      closedDate: payload.closedDate ? new Date(payload.closedDate) : null,
      createdTime: new Date(payload.createdTime),
      expectedClosingDate: payload.expectedClosingDate
        ? new Date(payload.expectedClosingDate)
        : null,
      lastActionDate: payload.lastActionDate
        ? new Date(payload.lastActionDate)
        : null,
      nextActionDate: payload.nextActionDate
        ? new Date(payload.nextActionDate)
        : null,
      updatedDate: payload.updatedDate ? new Date(payload.updatedDate) : null,
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const updateOpportunityInfo = createAsyncThunk<
  OpportunityInfo,
  OpportunityInfo,
  { rejectValue: APIReject }
>('opportunity/updateOpportunityInfo', async (model, thunkApi) => {
  const targetPests = model.targetPests.length
    ? model.targetPests.map((t) => t.id.toString()).toString()
    : ''
  if (checkNotSelectedCampaignIdAndSourceId(model.campaignId)) {
    model.campaignId = null
    model.campaign = null
  }

  if (checkNotSelectedCampaignIdAndSourceId(model.leadSourceId)) {
    model.leadSourceId = null
    model.leadSource = null
  }
  const response = await put(`${baseUrl}/updateOpportunity`, {
    ...model,
    targetPests,
  })
  if (response.ok) {
    const payload: OpportunityInfo = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const updateName = createAsyncThunk<
  OpportunityName,
  OpportunityName,
  { rejectValue: APIError }
>('opportunity/updateName', async (model, thunkApi) => {
  const response = await put(`${baseUrl}/updateName`, model)
  if (response.ok) {
    thunkApi.dispatch(getOpportunityDetail(model.id))
    thunkApi.dispatch(getOpportunities())
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== F E T C H   I S   C L O S E A B L E   &   R E A S O N S ==========
export const getOpportunityIsCloseable = createAsyncThunk<
  IOpportunityCloseableUpdateModel,
  number,
  { rejectValue: APIError }
>('opportunity/getIsCloseable', async (opportunityId, thunkApi) => {
  const response = await get(`${baseUrl}/isCloseable?id=${opportunityId}`)

  if (response.ok) {
    const payload: IOpportunityIsCloseableResponse = await response.json()
    const updateModel: IOpportunityCloseableUpdateModel = {
      id: opportunityId,
      closeRequirements: payload.requirements,
      isCloseable: payload.closeable,
    }
    return updateModel
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== U P D A T E  R E A S S I G N   L E A D  ==========
export const updateReassignOpportunity = createAsyncThunk<
  ISalesTeamReassignDTO,
  ISalesTeamReassignUpdate,
  { rejectValue: APIError }
>(
  'updateReassignOpportunity',
  async (data: ISalesTeamReassignUpdate, thunkApi) => {
    const response = await post(`${baseUrl}/Reassign`, data)

    if (response.ok) {
      const payload: IOpportunityDTO = await response.json()
      return payload
    }
    return thunkApi.rejectWithValue((await response.json()) as APIError)
  },
)

//========== A D D   C O N T A C T ==========
export const addContact = createAsyncThunk<
  IOpportunityContactUpdateResponse,
  IOpportunityContactUpdate,
  { rejectValue: APIValidationError }
>('opportunity/addContact', async (updateModel, thunkApi) => {
  const contact: IContactInput = {
    ...updateModel.contact,
    leadId: null,
    opportunityId: updateModel.opportunityId,
    isPrimaryContact: updateModel.isPrimaryContact,
  }
  const response = await post(`${contactBaseUrl}`, contact)
  if (response.ok) {
    const responseData = await response.json()
    const responseModel: IOpportunityContactUpdateResponse = {
      contact: responseData,
      isPrimaryContact: updateModel.isPrimaryContact,
      opportunityId: updateModel.opportunityId,
    }
    thunkApi.dispatch(getOpportunityDetail(updateModel.opportunityId))
    thunkApi.dispatch(getOpportunities())
    return responseModel
  }
  return thunkApi.rejectWithValue(await response.json())
})

//========== A T T A C H   C O N T A C T ==========
export const attachContact = createAsyncThunk<
  void,
  IOpportunityContactAttach,
  { rejectValue: APIValidationError }
>('opportunity/attachContact', async (model, thunkApi) => {
  const response = await post(`${secondaryContactBaseUrl}`, model)
  if (response.ok) {
    thunkApi.dispatch(getOpportunityDetail(model.opportunityId))
    thunkApi.dispatch(getOpportunities())
  }
})

//========== R E M O V E   C O N T A C T ==========
export const deleteContact = createAsyncThunk<
  IOpportunityContactDeleteModel,
  IOpportunityContactDeleteModel,
  { rejectValue: string }
>('opportunity/deleteContact', async (model, thunkApi) => {
  const response = await remove(`${secondaryContactBaseUrl}/${model.contactId}`)
  if (response.ok) {
    thunkApi.dispatch(getOpportunityDetail(model.opportunityId))
    thunkApi.dispatch(getOpportunities())
    return model
  }
  return thunkApi.rejectWithValue(await response.json())
})

//========== U P D A T E    C O N T A C T ==========
export const updateContact = createAsyncThunk<
  IOpportunityContactUpdateResponse,
  IOpportunityContactUpdate,
  { rejectValue: APIValidationError }
>('opportunity/updateContact', async (updateModel, thunkApi) => {
  const contact: IContactInput = {
    ...updateModel.contact,
    leadId: null,
    opportunityId: updateModel.opportunityId,
    isPrimaryContact: updateModel.isPrimaryContact,
  }
  const response = await put(`${contactBaseUrl}`, contact)
  if (response.ok) {
    const responseData = await response.json()
    const responseModel: IOpportunityContactUpdateResponse = {
      contact: responseData,
      isPrimaryContact: updateModel.isPrimaryContact,
      opportunityId: updateModel.opportunityId,
    }
    thunkApi.dispatch(getOpportunityDetail(updateModel.opportunityId))
    thunkApi.dispatch(getOpportunities())
    return responseModel
  }
  return thunkApi.rejectWithValue(await response.json())
})

// =========== S E T   P R I M A R Y   C O N T A C T ===========
export const setPrimaryContact = createAsyncThunk<
  IPrimaryContactModel,
  IPrimaryContactModel,
  { rejectValue: APIResponse }
>('opporuntity/setPrimaryContact', async (model, thunkApi) => {
  const response = await post(`${baseUrl}/setPrimaryContact`, model)
  if (response.ok) {
    thunkApi.dispatch(getOpportunityDetail(model.id))
    thunkApi.dispatch(getOpportunities())
    const updatedPrimaryContact: IPrimaryContactModel = await response.json()
    model.draftAgreementsUpdated = updatedPrimaryContact.draftAgreementsUpdated
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIResponse)
})

export const incrementOpportunityPaymentsCount = createAsyncThunk<
  number,
  number,
  { rejectValue: APIError }
>('opportunity/incrementPaymentsCount', async (id, thunkApi) => {
  const response = await post(
    `${baseUrl}/incrementPaymentsCount`,
    id.toString(),
  )

  if (response.ok) {
    return id
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

//========== P U S H   C O N T A C T   T O   L O C A T I O N ==========
export const pushContact = createAsyncThunk<
  number,
  IPushContactInput,
  { rejectValue: APIValidationError }
>('contact/update', async (model, thunkApi) => {
  const response = await post(`${contactBaseUrl}/pushContact`, model)

  if (response.ok) {
    thunkApi.dispatch(getOpportunityDetail(model.entityId))
    return model.entityId
  }
  return thunkApi.rejectWithValue(await response.json())
})

//#region bulk actions
export const bulkDeleteOpps = createAsyncThunk<
  void,
  number[],
  { rejectValue: APIError }
>('opportunity/bulk/delete', async (ids, thunkApi) => {
  const response = await remove(`${baseUrl}/bulkDelete`, ids)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})

export const bulkUpdateOppStatus = createAsyncThunk<
  { mode: BulkMode; model: BulkUpdateStatusDTO },
  { mode?: BulkMode; model: BulkUpdateStatusInput },
  { rejectValue: APIError }
>('opportunity/bulk/status', async ({ mode = 'validate', model }, thunkApi) => {
  const response = await post(`${baseUrl}/bulkUpdateStatus?mode=${mode}`, model)
  if (response.ok) return { mode, model: await response.json() }
  return thunkApi.rejectWithValue(await response.json())
})

export const bulkReopenOpps = createAsyncThunk<
  { mode: BulkMode; model: BulkReopenDTO },
  { mode?: BulkMode; model: BulkReopenInput },
  { rejectValue: APIError }
>('opportunity/bulk/reopen', async ({ mode = 'validate', model }, thunkApi) => {
  const response = await post(`${baseUrl}/bulkReopen?mode=${mode}`, model)
  if (response.ok) return { mode, model: await response.json() }
  return thunkApi.rejectWithValue(await response.json())
})

export const bulkReassignOpps = createAsyncThunk<
  { mode: BulkMode; model: BulkReassignDTO },
  { mode?: BulkMode; model: BulkReassignInput },
  { rejectValue: APIError }
>(
  'opportunity/bulk/reassign',
  async ({ mode = 'validate', model }, thunkApi) => {
    const response = await post(`${baseUrl}/bulkReassign?mode=${mode}`, model)
    if (response.ok) return { mode, model: await response.json() }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const bulkCloseWinOpportunities = createAsyncThunk<
  { mode: BulkMode; model: BulkUpdateStatusDTO },
  { mode?: BulkMode; model: BulkCloseWinOpportunityInput },
  { rejectValue: APIError }
>(
  'opportunity/bulk/bulkCloseWin',
  async ({ mode = 'validate', model }, thunkApi) => {
    const response = await post(`${baseUrl}/bulkCloseWin?mode=${mode}`, model)
    if (response.ok) return { mode, model: await response.json() }
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const getLocationDetailsForOpportunity = createAsyncThunk<
  IServiceLocationDetails,
  ILocationDetailParams
>('Opportunity/locationDetailCard', async (model, thunkApi) => {
  const response = await get(
    `${baseUrl}/GetLocationDetails/${model.entityId}?locationId=${model.locationId}&branch=${model.branch}&branchId=${model.branchId}&divisionId=${model.divisionId}&locationTypeId=${model.locationTypeId}`,
  )
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const bulkUpdateNextActionDate = createAsyncThunk<
  void,
  IBulkNextActionDateInput,
  { rejectValue: APIError }
>('opportunity/bulk/UpdateNextActionDate', async (input, thunkApi) => {
  const response = await post(`${baseUrl}/bulkUpdateNextActionDate`, input)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})

export const bulkUpdateExpectedClosingDate = createAsyncThunk<
  { mode: BulkMode; model: BulkUpdateExpectedClosingDateDTO },
  { mode?: BulkMode; model: BulkUpdateExpectedClosingDateInput },
  { rejectValue: APIError }
>(
  'opportunity/bulk/updateExpextedClosingDate',
  async ({ mode = 'validate', model }, thunkApi) => {
    const response = await post(
      `${baseUrl}/bulkUpdateExpectedClosingDate?mode=${mode}`,
      model,
    )
    if (response.ok) return { mode, model: await response.json() }
    return thunkApi.rejectWithValue(await response.json())
  },
)
//#endregion

// ========== U P D A T E   T A X   C O D E ==========
export const updateOpportunityTaxCode = createAsyncThunk<
  IServiceTax,
  IServiceTaxUpdate,
  { rejectValue: APIError }
>('updateOpportunityTaxCode', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/UpdateTaxRate`, data)
  if (response.ok) {
    const payload: IServiceTax = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== U P D A T E   R E F E R R A L ==========
export const updateReferral = createAsyncThunk<
  IReferralUpdate,
  IReferralUpdate,
  { rejectValue: APIError }
>('lead/updateReferral', async (model, thunkApi) => {
  const postModel: IReferralUpdatePostModel = {
    id: model.entityId,
    ...model.referral,
    referralType: model.referral ? model.referral.referralType : null,
  }
  const response = await put(`${baseUrl}/updateReferral`, postModel)
  if (response.ok) {
    await thunkApi.dispatch(getOpportunityDetail(model.entityId))
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== G E T   C O M P L E T I O N   D E T A I L S ==========
export const getCompletionDetails = createAsyncThunk<
  IGetCompletionDetailsResponseModel,
  number,
  { rejectValue: APIError }
>('opportunity/getCompletionDetails', async (opportunityId, thunkApi) => {
  const response = await get(
    `${baseUrl}/${opportunityId}${ServiceConfig.opportunity.completionDetails}`,
  )

  if (response.ok) {
    const payload: IExternalEntityServicesModel[] = await response.json()
    return {
      opportunityId: opportunityId,
      services: payload,
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== G E T   A C T I V E   S E R V I C E S ==========
export const getActiveServices = createAsyncThunk<
  IActiveServiceDetail[],
  number,
  { rejectValue: APIError }
>('opportunity/getActiveServices', async (opportunityId, thunkApi) => {
  const response = await get(
    `${baseUrl}/${opportunityId}${ServiceConfig.opportunity.activeServices}`,
  )

  if (response.ok) {
    return (await response.json()) as IActiveServiceDetail[]
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== G E T   R E J E C T E D   N O T E ==========
export const getRejectedNote = createAsyncThunk<
  IRejectedNoteDTO,
  number,
  { rejectValue: APIError }
>('opportunity/getRejectedNote', async (opportunityId, thunkApi) => {
  const response = await get(
    `${baseUrl}/${opportunityId}${ServiceConfig.opportunity.rejectedNote}`,
  )

  if (response.ok) {
    const payload: IRejectedNoteDTO = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== S P L I T   O P P O R T U N I T Y ==========
export const splitLeadOrOpportunity = createAsyncThunk<
  void,
  SplitOpportunityInput,
  { rejectValue: APIValidationError }
>('opportunity/split', async (model, thunkApi) => {
  const url = `${baseUrl}${ServiceConfig.opportunity.split}`
  const response = await post(`${url}`, model)
  if (response.ok) {
    return response.json()
  }
  return thunkApi.rejectWithValue((await response.json()) as APIValidationError)
})
